/* TR Hero slideshow: sections/tr-hero-slideshow.liquid
   Crossfade slides, dots, swipe, keyboard arrows, autoplay.
   Autoplay pauses on hover/focus, when the tab is hidden and when the banner is scrolled out of view. */

if (!customElements.get('tr-hero-slideshow')) {
  class TrHeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.tr-hero__slide')];
      if (this.slides.length < 2) return;

      this.track = this.querySelector('[data-tr-hero-track]');
      this.dots = [...this.querySelectorAll('[data-tr-hero-dot]')];
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseInt(this.dataset.speed, 10) || 5000;
      this.timer = null;
      this.paused = false;
      this.inView = true;

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.go(this.index - 1, true);
        if (event.key === 'ArrowRight') this.go(this.index + 1, true);
      });

      this.bindSwipe();

      this.addEventListener('mouseenter', () => this.setPaused(true));
      this.addEventListener('mouseleave', () => this.setPaused(false));
      this.addEventListener('focusin', () => this.setPaused(true));
      this.addEventListener('focusout', () => this.setPaused(false));
      document.addEventListener('visibilitychange', () => this.restart());

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          this.restart();
        }).observe(this);
      }

      // Theme editor: show the selected slide
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.indexOf(event.target);
        if (i > -1) this.go(i, false);
        this.setPaused(true);
      });
      this.addEventListener('shopify:block:deselect', () => this.setPaused(false));

      this.arm();
    }

    // Reveal slides 2+ (and so start their lazy image downloads) only after the page has loaded,
    // then start autoplay. Until then only slide 1 is in the DOM flow.
    arm() {
      const enable = () => {
        this.classList.add('is-armed');
        this.restart();
      };
      const whenIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(enable, { timeout: 2000 }) : setTimeout(enable, 200));

      if (window.Shopify?.designMode || document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true });
    }

    bindSwipe() {
      this.track.addEventListener('pointerdown', (event) => {
        this.startX = event.clientX;
        this.pointerId = event.pointerId;
        this.dragging = false;
      });

      this.track.addEventListener('pointermove', (event) => {
        if (this.startX == null || event.pointerId !== this.pointerId) return;
        if (this.dragging || Math.abs(event.clientX - this.startX) < 10) return;
        // Capture only for a real drag, so a plain click still reaches the slide link
        this.dragging = true;
        this.track.setPointerCapture(this.pointerId);
      });

      this.track.addEventListener('pointerup', (event) => {
        if (this.startX == null) return;
        const delta = event.clientX - this.startX;
        const wasDragging = this.dragging;
        this.startX = null;
        this.dragging = false;
        if (!wasDragging || Math.abs(delta) < 40) return;
        // A swipe that ends over a linked slide must not also open the link
        this.suppressClick = true;
        this.go(this.index + (delta < 0 ? 1 : -1), true);
      });

      this.track.addEventListener('pointercancel', () => {
        this.startX = null;
        this.dragging = false;
      });

      this.track.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          this.suppressClick = false;
          event.preventDefault();
        },
        true
      );
    }

    go(newIndex, isManual) {
      if (!this.classList.contains('is-armed')) this.classList.add('is-armed');
      const total = this.slides.length;
      const target = ((newIndex % total) + total) % total;
      if (target === this.index) return;

      const outgoing = this.slides[this.index];
      const incoming = this.slides[target];
      this.index = target;

      incoming.removeAttribute('inert');
      incoming.classList.add('is-active');
      outgoing.classList.remove('is-active');
      outgoing.setAttribute('inert', '');

      this.dots.forEach((dot, i) => {
        const active = i === target;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      if (isManual) this.restart();
    }

    setPaused(paused) {
      this.paused = paused;
      this.restart();
    }

    restart() {
      window.clearInterval(this.timer);
      if (!this.classList.contains('is-armed')) return;
      if (!this.autoplay || this.paused || !this.inView || document.hidden) return;
      this.timer = window.setInterval(() => this.go(this.index + 1, false), this.speed);
    }
  }

  customElements.define('tr-hero-slideshow', TrHeroSlideshow);
}
